import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { CanLoad, Route, Router } from '@angular/router';
import { Observer, Observable, combineLatest, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { isPlatformServer } from '@angular/common';
import * as ngrxTypes from '../store/root.types';
import { S3Service } from '../services/s3/s3.service';
import { SitemapActions } from '../store/actions/sitemap.actions';
import { RESPONSE } from '../../shared/tokens';
import { Sitemap, Redirect } from '../../shared/interfaces';
import { RedirectsActions } from '../store/actions/redirects.actions';
import { WordpressService } from '../services/wordpress/wordpress.service';
import { Logger } from '../services/logger/logger.service';

type DynamicRoutes = [Sitemap, Redirect[]];

/**
 * PreloadSitemapGuard
 * Prevents initial router navigation until dynamic routes can be loaded from sitemap.
 */
@Injectable()
export class PreloadSitemapGuard implements CanLoad {
  subs: Subscription[] = [];

  constructor(
    private s3Service: S3Service,
    private store: Store<ngrxTypes.AppState>,
    private sitemapActions: SitemapActions,
    private redirectsActions: RedirectsActions,
    private wordpressService: WordpressService,
    private logger: Logger,
    private router: Router,
    @Inject(RESPONSE) private response: any,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * canLoad - Block Route navigation until sitemap data can be loaded into the base route's data for later injection
   * @param route {Route} The route requesting to load
   */
  canLoad(route: Route): Observable<boolean> | boolean {
    return new Observable((observer: Observer<boolean>) => {
      this.wordpressService.getMenuConfig();
      this.wordpressService.getOptionsConfig();
      this.wordpressService.getAdminbarConfig();
      this.getSitemap(observer);
      this.getRedirects();

      combineLatest([this.store.select(ngrxTypes.sitemap), this.store.select(ngrxTypes.redirects)]).subscribe(
        (dynamicRoutes: DynamicRoutes) => {
          if (dynamicRoutes && dynamicRoutes[0] != null && dynamicRoutes[1] != null) {
            if (this.subs) {
              this.subs.forEach((sub) => sub.unsubscribe());
            }
            route.data = {
              sitemap: dynamicRoutes[0],
              redirects: dynamicRoutes[1],
            };
            observer.next(true);
            observer.complete();
          }
        },
      );
    });
  }

  getSitemap(observer: Observer<boolean>) {
    this.subs.push(
      this.store.select(ngrxTypes.sitemap).subscribe((sitemap) => {
        if (!sitemap) {
          this.s3Service.getFile('/sitemap-data.json', true).subscribe(
            (config: Sitemap) => {
              this.store.dispatch(this.sitemapActions.setSitemap(config));
            },
            (error) => {
              this.logger.logWarn('Loading error route as we failed to load sitemap data');
              this.router.navigate(['/error', { title: error.message, info: 'Sitemap data failed to load' }], {
                skipLocationChange: true,
              });
              this.set500StatusCode();
              observer.error(error);
            },
          );
        }
      }),
    );
  }

  getRedirects() {
    this.subs.push(
      this.store.select(ngrxTypes.redirects).subscribe((redirects) => {
        if (!redirects) {
          this.s3Service.getFile('/redirections.json', true).subscribe(
            (config: Redirect[]) => {
              this.store.dispatch(this.redirectsActions.setRedirects(config));
            },
            () => {
              this.set500StatusCode();
              // gracefully handle redirections.json failing - this isn't an app crashing worthy error
              this.store.dispatch(this.redirectsActions.setRedirects([]));
            },
          );
        }
      }),
    );
  }

  /**
   * Return 500 status code so Fastly will continue to serve stale content if
   * a critical file is missing
   */
  private set500StatusCode() {
    if (isPlatformServer(this.platformId)) {
      this.response.status(500);
    }
  }
}
